import axios from 'axios';

// NOTE: Unable to import directly from "_resources" due to circular dependencies
import { RESOURCE_TYPES } from '@techchange/resources';

const axiosClient = axios.create({
	withCredentials: true,
});

/**
 * Publish Statement
 *
 * Publish a statement to the LRS.
 *
 * @param   {object} statement - The LRS Statement
 * @returns {Promise} - The promise of the axios post.
 **/
const publishLRSStatement = (statement) => {

	const requestData = Object.assign({}, statement);

	// Check if the statement includes the object data
	if (requestData.object) {

		const object = Object.assign({}, requestData.object);

		Reflect.deleteProperty(object, 'children');

		requestData.object = object;

	}

	// Check if the statement includes the parent object data
	if (requestData.parent_object) {

		const parentObject = Object.assign({}, requestData.parent_object);

		Reflect.deleteProperty(parentObject, 'children');

		requestData.parent_object = parentObject;

	}

	return axiosClient.post('/api/v2/lrs/statements', requestData);

};

/**
 * Publish a list of statements to the LRS.
 *
 * @param   {object} statements - The list of LRS Statements to publish in bulk.
 * @returns {Promise} - The promise of the axios post.
 **/
const bulkPublishLRSStatement = (statements) => {

	const requestList = [];

	statements.forEach((statement) => {

		const requestData = Object.assign({}, statement);

		const object = Object.assign({}, requestData.object || {});
		const parentObject = Object.assign({}, requestData.parent_object || {});

		Reflect.deleteProperty(object, 'children');
		Reflect.deleteProperty(parentObject, 'children');

		requestData.object = object;
		requestData.parent_object = parentObject;

		requestList.push(requestData);

	});

	return axiosClient.post('/api/v2/lrs/statements/batch', { statements: requestList });

};

/**
 * Notify the LRS that the user clicked on this module.
 *
 * @param {object} resource  The module resource.
 * @param {object} parent    The parent session resource.
 *
 */
const postLRSModuleClick = (resource, parent) => {

	publishLRSStatement({
		action: 'clicked',
		object_type: 'navigation-control',
		object: resource,
		object_id: resource.id,
		object_title: resource.title,
		object_description: JSON.stringify(resource.content),
		object_user_id: resource.creator_id,
		parent_object: parent,
		parent_object_type: parent.type,
		parent_object_id: parent.id,
		parent_object_title: parent.title,
		parent_object_description: JSON.stringify(parent.content),
		parent_object_user_id: parent.creator_id,
		scope: resource.scope,
		scope_id: resource.scope_id,
		organization_id: resource.organization_id,
	}).catch((error) => {
		console.error(error);
	});

};

/**
 * Notify the LRS that the user viewed this module.
 *
 * @param {object} resource  The module resource.
 * @param {object} parent    The parent session resource.
 *
 */
const postLRSModuleView = (resource, parent) => {

	publishLRSStatement({
		action: 'viewed',
		object_type: 'module',
		object: resource,
		object_id: resource.id,
		object_title: resource.title,
		object_description: JSON.stringify(resource.content),
		object_user_id: resource.creator_id,
		parent_object: parent,
		parent_object_type: parent.type,
		parent_object_id: parent.id,
		parent_object_title: parent.title,
		parent_object_description: JSON.stringify(parent.content),
		parent_object_user_id: parent.creator_id,
		scope: resource.scope,
		scope_id: resource.scope_id,
		organization_id: resource.organization_id,
	}).catch((error) => {
		console.error(error);
	});

};

/**
 * Notify the LRS that the user clicked on this submodule.
 *
 * @param {object} resource  The submodule resource.
 * @param {object} parent    The parent module/submodule resource.
 *
 */
const postLRSSubmoduleClick = (resource, parent) => {

	publishLRSStatement({
		action: 'clicked',
		object_type: 'navigation-control',
		object: resource,
		object_id: resource.id,
		object_title: resource.title,
		object_description: JSON.stringify(resource.content),
		object_user_id: resource.creator_id,
		parent_object: parent,
		parent_object_type: parent.type,
		parent_object_id: parent.id,
		parent_object_title: parent.title,
		parent_object_description: JSON.stringify(parent.content),
		parent_object_user_id: parent.creator_id,
		scope: resource.scope,
		scope_id: resource.scope_id,
		organization_id: resource.organization_id,
	}).catch((error) => {
		console.error(error);
	});

};

/**
 * Notify the LRS that the user viewed this submodule.
 *
 * @param {object} resource  The submodule resource.
 * @param {object} parent    The parent module/submodule resource.
 *
 */
const postLRSSubmoduleView = (resource, parent) => {

	publishLRSStatement({
		action: 'viewed',
		object_type: 'submodule',
		object: resource,
		object_id: resource.id,
		object_title: resource.title,
		object_description: JSON.stringify(resource.content),
		object_user_id: resource.creator_id,
		parent_object: parent,
		parent_object_type: parent.type,
		parent_object_id: parent.id,
		parent_object_title: parent.title,
		parent_object_description: JSON.stringify(parent.content),
		parent_object_user_id: parent.creator_id,
		scope: resource.scope,
		scope_id: resource.scope_id,
		organization_id: resource.organization_id,
	}).catch((error) => {
		console.error(error);
	});

};

/**
 * Publishes statements to the LRS for video related actions. Used
 * for tracking playing, pausing, and finishing videos including the
 * current time and total time of the video.
 *
 * @param {Object} resource The resource
 * @param {Object} parent The resource parent
 * @param {Object} meta The metadata specific to the video to report to the LRS
 * @param {Object} meta.status The LRS action
 * @param {Object} meta.totalTime The total time or duration of the video
 * @param {Object} meta.currentTime The current time of the video
 */
const postLRSVideoWatch = (resource, parent, meta = {}) => {

	if (resource.type === RESOURCE_TYPES.VIDEO) {

		publishLRSStatement({
			action: meta.status,
			object_type: resource.type,
			object: resource,
			object_id: resource.id,
			object_title: resource.title,
			object_description: resource.title,
			object_user_id: resource.creator_id,
			parent_object: parent,
			parent_object_type: parent.type,
			parent_object_id: parent.id,
			parent_object_title: parent.title,
			parent_object_description: parent.title,
			parent_object_user_id: parent.creator_id,
			meta: {
				duration: meta.totalTime,
				current_time: meta.currentTime,
			},
			scope: resource.scope,
			scope_id: resource.scope_id,
			organization_id: resource.organization_id,
		}).catch((error) => {
			console.error(error);
		});

	}

};

const postLRSSelfComplete = (resource, parent) => {

	publishLRSStatement({
		action: 'marked-complete',
		object_type: resource.type,
		object: resource,
		object_id: resource.id,
		object_title: resource.title,
		object_description: JSON.stringify(resource.content),
		object_user_id: resource.creator_id,
		parent_object_type: parent.type,
		parent_object: parent,
		parent_object_id: parent.id,
		parent_object_title: parent.title,
		parent_object_description: JSON.stringify(parent.content),
		parent_object_user_id: parent.creator_id,
		scope: resource.scope,
		scope_id: resource.scope_id,
		organization_id: resource.organization_id,
	}).catch((error) => {
		console.error(error);
	});

};


const postLRSLinkClick = (resource, parent, meta = {}) => {

	let objectResource;

	let parentObjectResource;

	// Check if the link click was for an image
	if (resource.type === 'image') {

		objectResource = resource;
		parentObjectResource = parent;

	// Otherwise, find the child resource of the slide that matches the link
	} else if (resource.type === 'slide' && resource.children) {

		objectResource = resource.children.find((child) => child.id === meta.id);
		parentObjectResource = resource;

	}

	return new Promise((resolve) => {

		// Check if we found a matching resource for the link that was clicked
		if (objectResource) {

			return publishLRSStatement({
				action: 'clicked',
				object_type: objectResource.type,
				object: objectResource,
				object_id: objectResource.id,
				object_title: objectResource.title,
				object_description: objectResource.title,
				object_user_id: objectResource.creator_id,
				parent_object_type: parentObjectResource.type,
				parent_object: parentObjectResource,
				parent_object_id: parentObjectResource.id,
				parent_object_title: parentObjectResource.title,
				parent_object_description: parentObjectResource.title,
				parent_object_user_id: parentObjectResource.creator_id,
				scope: objectResource.scope,
				scope_id: objectResource.scope_id,
				organization_id: objectResource.organization_id,
			}).then(() => resolve());

		}

		// Otherwise, let the user continue to the link even if tracking failed for some reason...
		console.error(`No match found for link id=${meta.id} in slide resource id=${resource.id}`);
		return resolve();

	});

};

const postLRSSlideView = (resource, parent) => {

	if (resource.type === 'slide') {

		publishLRSStatement({
			action: 'viewed',
			object_type: resource.type,
			object: resource,
			object_id: resource.id,
			object_title: resource.title,
			object_description: JSON.stringify(resource.content),
			object_user_id: resource.creator_id,
			parent_object_type: parent.type,
			parent_object: parent,
			parent_object_id: parent.id,
			parent_object_title: parent.title,
			parent_object_description: JSON.stringify(parent.content),
			parent_object_user_id: parent.creator_id,
			scope: resource.scope,
			scope_id: resource.scope_id,
			organization_id: resource.organization_id,
		}).catch((error) => {
			console.error(error);
		});

	}

};

export {
	postLRSLinkClick,
	postLRSModuleClick,
	postLRSModuleView,
	postLRSSubmoduleClick,
	postLRSSubmoduleView,
	postLRSSelfComplete,
	postLRSSlideView,
	postLRSVideoWatch,
	publishLRSStatement,
	bulkPublishLRSStatement,
};
